// How often the board is allowed to draw.
//
// The render loop is on demand — a turn-based board is static most of the time
// and an idle tab should not spin the GPU — but a board that *is* busy draws on
// every frame the display offers, which on a watched scenario is the whole
// game. That is the case this exists for: a machine-vs-machine board is a
// planner and a renderer competing for the same cores, and the planner is the
// half doing the work somebody is watching for. Frames the renderer does not
// take are frames the search gets.
//
// **A cap skips the frame, it does not draw a cheaper one.** Everything the
// loop does is either advancing an animation by `dt` or drawing the result, and
// both are the frame — so the honest way to draw less often is to do neither
// and let the next one cover the gap. Which is why a skipped frame's time is
// carried rather than lost: the time still happened.
//
// **Pure, because the thing that goes wrong here is arithmetic** — and it goes
// wrong quietly. A cap landing on the wrong side of the display's own period
// does not fail, it silently halves the frame rate: the board still animates,
// still at the right speed, just at half the number the player picked, with
// nothing on screen saying so.

/**
 * The frame caps offered. `0` is off, which is the default and the shipping
 * behaviour.
 *
 * Short and coarse on purpose: this is a ceiling rather than a target, so the
 * useful question is "half", "a quarter" or "just enough to follow", and a
 * value between two of those buys nothing a person can act on.
 */
export const FRAME_CAPS = [0, 15, 30, 60] as const;

/**
 * The longest step an animation is ever advanced by, in seconds.
 *
 * A tab that was backgrounded comes back with a gap of whole seconds in it, and
 * an unclamped `dt` teleports everything mid-flight. So there is a ceiling —
 * and what it must be above is **an ordinary slow frame**, which is the thing
 * that was got wrong.
 *
 * **At a twentieth of a second this clamp was slower than a large board draws,
 * and that desynchronises the two clocks this game runs on.** The renderer
 * advances animations by `dt`; the driver deals a turn out on `setTimeout`,
 * which is wall clock and cannot be clamped. While a frame costs less than the
 * ceiling the two agree. Past it they do not: a board expensive enough to take
 * 80ms a frame advanced 50ms of animation for every 80ms the driver counted,
 * so the pieces ran at five eighths speed while the commands kept arriving on
 * time — and the next move landed on an animation that had not finished.
 *
 * **Which is exactly why it only showed up uncapped.** `stepClamp` raises the
 * ceiling to a cap's own interval, so any cap at all lifted it clear of the
 * frame time and the two clocks agreed again; uncapped, the ceiling stayed at
 * a twentieth and a big board sat the wrong side of it. Reported as moves
 * playing too fast on the default setting and stalling on larger boards, which
 * is one fault wearing two faces.
 *
 * A fifth of a second is above any frame a board that is still drawing takes —
 * 200ms a frame is five a second, which is broken rather than slow — and well
 * below the whole seconds a backgrounded tab comes back with, which is the case
 * the ceiling is for. It is deliberately *not* sized against an animation's own
 * length: nothing here is trying to make a gap look good, only to stop it
 * teleporting.
 */
export const MAX_STEP = 0.2;

/**
 * Is a frame due, given the time banked since the last one that drew?
 *
 * Trivial on its own; what makes the cap land is `nextCarry` below, which is
 * the half that keeps `banked` from being reset to nought every time.
 */
export const frameDue = (banked: number, interval: number): boolean =>
  interval <= 0 || banked >= interval;

/**
 * What to bank after drawing, having drawn with `banked` seconds accumulated.
 *
 * **The remainder is kept, and that is the whole design.** The obvious reading
 * is to reset the accumulator on every drawn frame, and it fails in both
 * directions at once:
 *
 *  - **Undershoot, on the commonest setting there is.** Cap at the rate the
 *    display already runs at and a gap of 16.6ms against an interval of
 *    16.667ms is a skip — so every other frame goes and a cap of 60 plays at
 *    30. Real `requestAnimationFrame` jitter is far larger than the float
 *    error, so this is not a corner case, it is most frames.
 *  - **Nothing between the display's own divisors.** Reset, a cap of 40 on a
 *    60Hz display can only ever be 30: there is no third of a frame to draw.
 *    Carried, it draws two frames then one then two, which is exactly 40.
 *
 * Carrying fixes both without a tolerance to tune, because the error each frame
 * is put back rather than thrown away — so the *average* rate is the cap, at
 * any display period.
 *
 * **A tab that was asleep banks nothing.** More than a whole interval left over
 * means frames were missed, and the missed ones are gone; banking them buys a
 * burst of draws of a board that has already settled.
 */
export function nextCarry(banked: number, interval: number): number {
  if (interval <= 0) return 0;
  const left = banked - interval;
  return left >= interval || !(left > 0) ? 0 : left;
}

/**
 * The longest step to advance by under this cap.
 *
 * **A cap is a ceiling on how often the board draws and never a claim about how
 * fast time passes**, so a cap slower than `MAX_STEP` has to lift the ceiling
 * with it — otherwise a cap of 4 would advance a fifth of a second four times a
 * second and play the whole board at four fifths speed.
 *
 * Every cap the screen currently offers is *faster* than `MAX_STEP`, so today
 * this returns the ceiling unchanged for all of them. That is the fix rather
 * than a redundancy: this function used to be the only thing lifting the
 * ceiling clear of a slow frame, which meant the uncapped setting — the
 * default, and the one with no interval to lift it — was the single
 * configuration where the clamp bit an ordinary frame. It stays because the
 * relationship it maintains is the load-bearing one, and a slower cap is one
 * entry in `FRAME_CAPS` away.
 */
export const stepClamp = (interval: number): number => Math.max(MAX_STEP, interval);

/**
 * The lowest a health reading may report, whatever the frames say.
 *
 * Health stretches the driver's step, so an unbounded reading is an unbounded
 * wait — and the one board that produces a terrible reading is the one nobody
 * wants frozen. A quarter is four times the ordinary step, which is far more
 * headroom than a machine that is merely struggling needs and still lands the
 * next command this side of anybody giving up.
 */
export const MIN_CLOCK_HEALTH = 0.25;

/**
 * A frame longer than this was not a slow frame.
 *
 * It is a tab nobody was looking at, which is the case `MAX_STEP` exists for —
 * and it says nothing about how fast the machine draws, so folding it into the
 * average would report a healthy board as sick for the next few seconds every
 * time somebody switched away and back.
 */
export const ASLEEP_FRAME = 1;

/**
 * How much animation time the board actually got, per second of wall clock.
 *
 * **The two clocks, as one number.** The renderer advances an animation by
 * `min(frameInterval, stepClamp(...))`; the driver deals its commands on
 * `setTimeout`, which is wall clock and cannot be clamped. While every frame
 * comes in under the ceiling the two agree and this is 1. Once frames cross it
 * they do not: animation time accrues more slowly than the driver counts, so
 * each command lands on an animation that has not finished, the piece re-keys
 * and snaps to where it was going — and a board whose motion is being cut off
 * reads as *speeding up*, which is the opposite of what is wrong with it.
 *
 * Measured on a watched scenario at a constant sixty-four cells: 0.98 in the
 * first minute and 0.66 in the twenty-fifth, with `plies/s` flat throughout.
 * The game was not playing faster; its animations were being given a third
 * less time than the driver was allowing them.
 *
 * **A measurement, not a fix for what makes the frames slow.** What it buys is
 * that the driver can stop outrunning a board it has no other way to hear from.
 */
export function clockHealth(advanced: number, elapsed: number): number {
  if (!(elapsed > 0) || !(advanced > 0)) return 1;
  const ratio = advanced / elapsed;
  if (!Number.isFinite(ratio)) return 1;
  return Math.min(1, Math.max(MIN_CLOCK_HEALTH, ratio));
}
